import { useState, useEffect } from "react";
import { money, x2, num, Delta, roasTone } from "./roasUtil";

const Kpi = ({ label, v, sub, tone, testid, children }) => (
  <div className="panel p-3 min-w-[140px] flex-1" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-[var(--text-dim)]">{label}</div>
    <div className={`text-xl font-semibold tabular-nums mt-0.5 ${tone || ""}`}>{v}</div>
    <div className="flex items-center gap-2 mt-0.5 min-h-[14px]">{children}{sub && <span className="text-[10px] text-[var(--text-faint)]">{sub}</span>}</div>
  </div>
);

function TargetInput({ target, onSave }) {
  const [v, setV] = useState(target || "");
  useEffect(() => { setV(target || ""); }, [target]);
  return (
    <div className="panel p-3 min-w-[140px] flex-1" data-testid="roas-target-card">
      <div className="text-[10px] uppercase tracking-wider text-[var(--text-dim)]">Target ROAS</div>
      <div className="flex items-center gap-1 mt-0.5">
        <input type="number" step="0.1" min="0" className="w-20 px-2 py-0.5 rounded border text-lg font-semibold tabular-nums bg-transparent" value={v}
          placeholder="set" onChange={(e) => setV(e.target.value)} onBlur={() => Number(v || 0) !== Number(target || 0) && onSave(Number(v || 0))}
          onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} data-testid="roas-target-input" />
        <span className="text-lg">x</span>
      </div>
      <div className="text-[10px] text-[var(--text-faint)] mt-0.5">Default for every month</div>
    </div>
  );
}

export function RoasKpis({ d, onTarget }) {
  const t = d.totals, p = d.previous?.totals;
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2" data-testid="roas-kpis">
        <Kpi label="Overall ROAS" v={x2(t.roas)} tone={roasTone(t.roas, t.target_roas)} testid="kpi-roas" sub={p ? `prev ${x2(p.roas)}` : null}><Delta cur={t.roas} prev={p?.roas} /></Kpi>
        <Kpi label="Ad spend" v={money(t.spend)} testid="kpi-spend" sub={p ? `prev ${money(p.spend)}` : null}><Delta cur={t.spend} prev={p?.spend} invert /></Kpi>
        <Kpi label="Sales (deal value)" v={money(t.revenue)} testid="kpi-revenue" sub={p ? `prev ${money(p.revenue)}` : null}><Delta cur={t.revenue} prev={p?.revenue} /></Kpi>
        <Kpi label="Clients won" v={num(t.won)} testid="kpi-won" sub={t.conv_pct != null ? `${t.conv_pct}% of leads` : null}><Delta cur={t.won} prev={p?.won} /></Kpi>
        <TargetInput target={d.totals.target_roas} onSave={onTarget} />
        <Kpi label={t.deficit ? "Sales short of target" : "Target"} v={t.target_roas ? (t.deficit ? money(t.deficit) : "Hit") : "\u2014"}
          tone={t.target_roas ? (t.deficit ? "text-[var(--breach)]" : "text-[var(--ok)]") : ""} testid="kpi-deficit"
          sub={t.target_roas ? `need ${money(t.required_revenue)}` : "set a target"} />
      </div>
      <div className="flex flex-wrap gap-2">
        <Kpi label="CRM leads" v={num(t.leads)} testid="kpi-leads" sub={`Meta counted ${num(t.meta_leads)}`}><Delta cur={t.leads} prev={p?.leads} /></Kpi>
        <Kpi label="Cost per lead" v={money(t.cpl)} testid="kpi-cpl"><Delta cur={t.cpl} prev={p?.cpl} invert /></Kpi>
        <Kpi label="Cost per client" v={money(t.cac)} testid="kpi-cac"><Delta cur={t.cac} prev={p?.cac} invert /></Kpi>
        <Kpi label="Avg deal" v={money(t.avg_deal)} testid="kpi-avg-deal"><Delta cur={t.avg_deal} prev={p?.avg_deal} /></Kpi>
        <Kpi label="Days to close" v={t.avg_days_to_close ?? "\u2014"} testid="kpi-days"><Delta cur={t.avg_days_to_close} prev={p?.avg_days_to_close} invert /></Kpi>
        <Kpi label="CTR" v={t.ctr_pct != null ? `${t.ctr_pct}%` : "\u2014"} testid="kpi-ctr" sub={`${num(t.clicks)} clicks`} />
      </div>
      {d.with_test && (
        <div className="flex flex-wrap gap-2" data-testid="roas-with-test">
          <Kpi label="ROAS incl. test" v={x2(d.with_test.roas)} tone={roasTone(d.with_test.roas, t.target_roas)} testid="kpi-roas-with-test"
            sub={`spend ${money(d.with_test.spend)}`} />
          <Kpi label="Test spend" v={money(d.with_test.test_spend)} testid="kpi-test-spend" sub={`${d.with_test.test_campaigns} test campaign${d.with_test.test_campaigns > 1 ? "s" : ""}`} />
          <Kpi label="Test sales / ROAS" v={money(d.with_test.test_revenue)} testid="kpi-test-roas" sub={`ROAS ${x2(d.with_test.test_roas)}`} />
          <Kpi label={d.with_test.deficit ? "Short incl. test" : "Target incl. test"} v={t.target_roas ? (d.with_test.deficit ? money(d.with_test.deficit) : "Hit") : "\u2014"}
            tone={t.target_roas ? (d.with_test.deficit ? "text-[var(--breach)]" : "text-[var(--ok)]") : ""} testid="kpi-deficit-with-test" />
        </div>
      )}
    </div>
  );
}

export function RoasPace({ p }) {
  if (!p) return null;
  const pctDone = Math.round((p.days_elapsed / p.days_in_month) * 100);
  return (
    <section className="panel p-4" data-testid="roas-pace">
      <div className="text-sm font-medium mb-2">This month so far · day {p.days_elapsed} of {p.days_in_month}</div>
      <div className="h-1.5 rounded bg-[var(--panel-2)] mb-3"><div className="h-full rounded bg-[var(--ai)]" style={{ width: `${pctDone}%` }} /></div>
      <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <div>Spent <b className="tabular-nums">{money(p.spend_mtd)}</b> · on track for <b className="tabular-nums">{money(p.projected_spend)}</b></div>
        <div>Sales so far <b className="tabular-nums">{money(p.revenue_mtd)}</b></div>
        {p.target_roas ? (p.projected_deficit
          ? <div className="text-[var(--breach)]" data-testid="roas-pace-deficit">Need <b>{money(p.projected_deficit)}</b> more sales to hit {p.target_roas}x
            {p.deals_needed ? <> (~{p.deals_needed} deal{p.deals_needed > 1 ? "s" : ""})</> : null} · {money(p.daily_revenue_needed)}/day</div>
          : <div className="text-[var(--ok)]" data-testid="roas-pace-deficit">On track for {p.target_roas}x at projected spend</div>)
          : <div className="text-[var(--text-dim)]">Set a target ROAS to see the gap</div>}
      </div>
    </section>
  );
}
